import {useState} from 'react'
import useHandleError from 'utils/useHandleError';
import apiClient from 'utils/apiClient';
import {CustomerProps} from "pages/Customer/types";
import {config} from "config/config";

export const useCustomer = () => {
    const [handleError] = useHandleError();
    const [loading, setLoading] = useState<boolean>(false)

    const getCustomer = async (id: string): Promise<CustomerProps | undefined> => {
        setLoading(true);
        try {
            const response = await apiClient.request(`${config.api.routes.backend.customers}/${id}`, {}, 'GET');

            if ("error" in response) {
                throw response;
            }

            if ("data" in response) {
                return response.data;
            }

            return undefined;
        } catch (error) {
            handleError(error);
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    const getCustomers = async ()/** : Promise<CustomerProps | undefined>*/ => {
        setLoading(true);
        try {
            const response = await apiClient.request(`${config.api.routes.backend.selectCustomers}`, {}, 'GET');

            if ("error" in response) {
                throw response;
            }

            if ("customers" in response) {
                return response.customers;
            }

            return undefined;
        } catch (error) {
            handleError(error);
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    const getDepartments = async (id: string): Promise<{ data: any[]; maxPlannerSlots: number }> => {
        setLoading(true);
        try {
            const response = await apiClient.request(`/api/v1/customers/${id}/structure`, {}, 'GET');

            if ("error" in response) {
                throw response;
            }

            return {
                data: response.data ?? [],
                maxPlannerSlots: response.maxPlannerSlots ?? 8,
            };
        } catch (error) {
            handleError(error);
            return { data: [], maxPlannerSlots: 8 };
        } finally {
            setLoading(false);
        }
    }

    const getResourcePlanners = async (id: string): Promise<{ data: any[] }> => {
        setLoading(true);
        try {
            const response = await apiClient.request(`/api/v1/customers/${id}/resource-planners`, {}, 'GET');

            if ("error" in response) {
                throw response;
            }

            return { data: response.data ?? [] };
        } catch (error) {
            handleError(error);
            return { data: [] };
        } finally {
            setLoading(false);
        }
    }

    const getResourcePlannerSubordinates = async (
        customerId: string,
        plannerUserId: number,
        page: number = 1,
        pageSize: number = 10,
        search?: string,
    ): Promise<{ data: any[]; pagination: { total: number; current: number; pageSize: number } }> => {
        try {
            let url = `/api/v1/customers/${customerId}/resource-planners/${plannerUserId}/users`
                + `?pagination[current]=${page}&pagination[pageSize]=${pageSize}`;
            if (search && search.trim() !== '') {
                url += `&qu=${encodeURIComponent(search.trim())}`;
            }

            const response = await apiClient.request(url, [], 'GET');

            if ("error" in response) {
                throw response;
            }

            return {
                data: response.data ?? [],
                pagination: response.pagination ?? { total: 0, current: page, pageSize },
            };
        } catch (error) {
            handleError(error);
            return { data: [], pagination: { total: 0, current: page, pageSize } };
        }
    }

    const saveResourcePlanner = async (
        customerId: string,
        resourceData: { id: number; user_type_ids: (number | null)[] }
    ): Promise<{ data: any[]; maxPlannerSlots: number } | null> => {
        setLoading(true);
        try {
            const response = await apiClient.request(`/api/v1/customers/${customerId}/structure`, resourceData, 'PUT')

            if ("error" in response) {
                throw response;
            }

            return {
                data: response.data ?? [],
                maxPlannerSlots: response.maxPlannerSlots ?? 8,
            };
        } catch (error) {
            handleError(error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    return {
        getCustomer,
        getDepartments,
        saveResourcePlanner,
        getResourcePlanners,
        getResourcePlannerSubordinates,
        getCustomers,
        loading
    }
}
